import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkIcon from "@mui/icons-material/Link";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  AssociatedCampaignAsset,
  useCreateAssociatedCampaignAssetMutation,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { components } from "../../types/api";
import KeyValueEditor from "../common/KeyValueEditor";
import { useCopyToClipboard } from "../common/CopyablePanel";
import AssetDestinationsEditor, {
  DestinationDraft,
} from "./AssetDestinationsEditor";
import {
  cleanLinkRecord,
  toDestinationDrafts,
  toDestinationPayload,
  toLinkRecord,
  useAutoUtmBuilder,
} from "./assetUtils";
import SectionCard from "./inline/SectionCard";
import FieldGroup from "./inline/FieldGroup";
import InlineField from "./inline/InlineField";
import { SectionEmpty } from "./inline/inputs";
import { bindField, useInlineSection } from "./inline/useInlineSection";
import { extractApiErrorMessage } from "./inline/apiErrors";

type LinkMap = Record<string, string>;

interface AssetDraft {
  assetsLinks: LinkMap;
  copiesLinks: LinkMap;
  additionalLinks: LinkMap;
  destinations: DestinationDraft[];
}
type LinkKey = "assetsLinks" | "copiesLinks" | "additionalLinks";

const EMPTY_ASSET: AssetDraft = {
  assetsLinks: {},
  copiesLinks: {},
  additionalLinks: {},
  destinations: [],
};

/** Row of icon buttons that never trigger the field's own click-to-copy. */
const RowAction: React.FC<{
  title: string;
  onClick?: () => void;
  href?: string;
  children: React.ReactElement;
}> = ({ title, onClick, href, children }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      {...(href && {
        component: "a",
        href,
        target: "_blank",
        rel: "noopener noreferrer",
      })}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        onClick?.();
      }}
      sx={{ p: 0.4, color: "text.secondary" }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

const LinkList: React.FC<{ links: LinkMap }> = ({ links }) => {
  const { t } = useTranslation();
  const copy = useCopyToClipboard();
  return (
    <Stack spacing={0.5}>
      {Object.entries(links).map(([name, url]) => (
        <Stack key={name} direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, minWidth: 64, flexShrink: 0 }}
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            sx={{
              color: "info.main",
              textDecoration: "none",
              wordBreak: "break-all",
              fontWeight: 400,
              flex: 1,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {url}
          </Typography>
          {url && (
            <RowAction
              title={t("common.copy", "Copy")}
              onClick={() => copy(url)}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </RowAction>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

const DestinationList: React.FC<{ destinations: DestinationDraft[] }> = ({
  destinations,
}) => {
  const { t } = useTranslation();
  const copy = useCopyToClipboard();
  return (
    <Stack spacing={1.25}>
      {destinations.map((destination, index) => (
        <Stack key={destination.id} direction="row" spacing={1.25}>
          <Box
            sx={{
              width: 22,
              height: 22,
              mt: 0.25,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 700,
              bgcolor: index === 0 ? "text.primary" : "action.selected",
              color: index === 0 ? "background.paper" : "text.secondary",
            }}
          >
            {index + 1}
          </Box>
          <Box minWidth={0} flex={1}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="body2" fontWeight={700}>
                {destination.label ||
                  t("briefingsView.asset.untitledDestination", "Untitled")}
              </Typography>
              {index === 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("briefingsView.asset.primaryDestination", "Main")}
                  sx={{ height: 18, fontSize: 11 }}
                />
              )}
              {!destination.isAutoUtm && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("briefingsView.asset.manualUtm", "Manual UTM")}
                  sx={{ height: 18, fontSize: 11 }}
                />
              )}
            </Stack>
            {destination.url && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="body2"
                  component="a"
                  href={destination.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                  sx={{
                    color: "info.main",
                    textDecoration: "none",
                    wordBreak: "break-all",
                    fontWeight: 400,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {destination.url}
                </Typography>
                <RowAction
                  title={t("common.open", "Open")}
                  href={destination.url}
                >
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </RowAction>
              </Stack>
            )}
            {destination.utm && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ wordBreak: "break-all" }}
                >
                  {destination.utm}
                </Typography>
                <RowAction
                  title={t("common.copy", "Copy")}
                  onClick={() => copy(destination.utm)}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </RowAction>
              </Stack>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

interface AssociatedCampaignAssetSectionProps {
  briefingId: string;
  associatedId: string;
  asset: AssociatedCampaignAsset | null;
  associatedCampaign: components["schemas"]["AssociatedCampaignResponse"];
  briefing?: { campaignName?: string | null; name?: string | null } | null;
  canEdit: boolean;
  /** Reloads the campaign; resolves once fresh data is in. */
  onSaved: () => Promise<unknown>;
}

const AssociatedCampaignAssetSection: React.FC<
  AssociatedCampaignAssetSectionProps
> = ({
  briefingId,
  associatedId,
  asset,
  associatedCampaign,
  briefing,
  canEdit,
  onSaved,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [creating, setCreating] = useState(false);
  const [createAsset] = useCreateAssociatedCampaignAssetMutation();
  const buildAutoUtm = useAutoUtmBuilder(associatedCampaign, briefing);

  const base = useMemo<AssetDraft>(
    () =>
      asset && !creating
        ? {
            assetsLinks: toLinkRecord(asset.assetsLinks),
            copiesLinks: toLinkRecord(asset.copiesLinks),
            additionalLinks: toLinkRecord(asset.additionalLinks),
            destinations: toDestinationDrafts(asset),
          }
        : EMPTY_ASSET,
    [asset, creating]
  );

  const handleSave = async (draft: AssetDraft) => {
    try {
      await createAsset({
        briefingId,
        associatedId,
        assetsLinks: cleanLinkRecord(draft.assetsLinks),
        copiesLinks: cleanLinkRecord(draft.copiesLinks),
        additionalLinks: cleanLinkRecord(draft.additionalLinks),
        destinations: toDestinationPayload(draft.destinations, buildAutoUtm),
      }).unwrap();
      await onSaved();
      setCreating(false);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsEdit.assetUpdateSuccess",
            "Assets updated successfully"
          ),
          severity: "success",
        })
      );
      return true;
    } catch (error) {
      dispatch(
        setToastMessage({
          message:
            extractApiErrorMessage(error) ??
            t("briefingsEdit.assetUpdateError", "Failed to update assets"),
          severity: "error",
        })
      );
      return false;
    }
  };

  const section = useInlineSection<AssetDraft>({
    id: "asset",
    base,
    onSave: handleSave,
    forceEdit: creating,
    readOnly: !canEdit || (!asset && !creating),
  });
  const { draft } = section;

  const linkField = (
    key: LinkKey,
    label: string,
    icon: React.ReactElement,
    exampleKey: string,
    exampleValue: string,
    helper?: string
  ) => {
    const links = draft[key];
    const isEmpty = Object.keys(links).length === 0;
    return (
      <InlineField
        label={label}
        icon={icon}
        empty={isEmpty}
        value={<LinkList links={links} />}
        helper={section.isEditing(key) ? helper : undefined}
        input={
          <KeyValueEditor
            label=""
            initialValue={links}
            onChange={(value) => section.setField(key, value)}
            exampleKey={exampleKey}
            exampleValue={exampleValue}
          />
        }
        {...bindField(section, key, canEdit)}
      />
    );
  };

  const hasAsset = Boolean(asset) || creating;

  return (
    <SectionCard
      icon={<PermMediaOutlinedIcon />}
      title={t("briefingsView.assetSectionTitle", "Assets")}
      subtitle={t(
        "briefingsView.assetSectionSubtitle",
        "Creatividades, copies y URLs de destino"
      )}
      dirtyCount={section.dirtyCount}
      saving={section.isSaving}
      saveLabel={
        creating ? t("briefingsView.createAsset", "Create asset") : undefined
      }
      onSave={() => void section.save()}
      onDiscard={() => {
        section.discard();
        setCreating(false);
      }}
      createdAt={creating ? null : asset?.createdAt}
      updatedAt={creating ? null : asset?.updatedAt}
    >
      {!hasAsset ? (
        <SectionEmpty
          message={t(
            "briefingsView.assetNotFound",
            "This associated campaign does not have asset details yet."
          )}
          actionLabel={
            canEdit ? t("briefingsView.createAsset", "Create asset") : undefined
          }
          onAction={() => setCreating(true)}
        />
      ) : (
        <Stack spacing={3}>
          <FieldGroup
            title={t("briefingsView.groups.links", "Enlaces")}
            columns={3}
          >
            {linkField(
              "assetsLinks",
              t("briefingsView.asset.assetsLinks", "Assets links"),
              <FolderSharedOutlinedIcon />,
              "video",
              "https://examples.com/asset",
              t(
                "briefingsEdit.helper.assetsLinks",
                "Add links to creative assets like videos or images"
              )
            )}
            {linkField(
              "copiesLinks",
              t("briefingsView.asset.copiesLinks", "Copies links"),
              <DescriptionOutlinedIcon />,
              "doc",
              "https://docs.google.com/...",
              t("briefingsEdit.helper.copiesLinks", "Links to copy documents")
            )}
            {linkField(
              "additionalLinks",
              t("briefingsView.asset.additionalLinks", "Additional links"),
              <LinkIcon />,
              "other",
              "https://..."
            )}
          </FieldGroup>

          <FieldGroup
            title={t("briefingsView.groups.destinations", "Destinos")}
            columns={1}
          >
            <InlineField
              label={`${t("briefingsView.asset.destinations", "Destination URLs")}${
                draft.destinations.length > 0
                  ? ` · ${draft.destinations.length}`
                  : ""
              }`}
              icon={<AltRouteIcon />}
              empty={draft.destinations.length === 0}
              value={<DestinationList destinations={draft.destinations} />}
              input={
                <AssetDestinationsEditor
                  hideTitle
                  destinations={draft.destinations}
                  onChange={(value) => section.setField("destinations", value)}
                  buildAutoUtm={buildAutoUtm}
                />
              }
              {...bindField(section, "destinations", canEdit)}
            />
          </FieldGroup>
        </Stack>
      )}
    </SectionCard>
  );
};

export default AssociatedCampaignAssetSection;
